import { Injectable } from '@angular/core';
import { BookService } from '@studiobuki/web-core/lib/book-common';
import type { TCreateUserBookOmitKeys } from '@studiobuki/web-core/lib/firebase';
import { FirebaseService } from 'src/app/services/firebase.service';
import type { TBookMode } from '@studiobuki/web-core/lib/routing';
import { ROUTE_PATH_QUERY_PARAM_MODE_USER } from '@studiobuki/web-core/lib/routing';
import { LoaderService } from '@studiobuki/web-core/lib/loader';
import { Logger } from '@shared/common/logger';
import {
  INPUT_VALUE_STEP_CHARACTER,
  INPUT_VALUE_STEP_MESSAGE,
  INPUT_VALUE_STEP_PHOTO,
  INPUT_VALUE_STEP_STORY,
} from '@shared/common/product';
import { PHOTO_TOKYO_METRO_LETS_MAKE_METRO } from '@shared/jp/product';
import type { IBookTMLetsMakeMetroData } from '@shared/jp/models';
import LetsMakeMetro from 'src/app/data/books/LetsMakeMetro';
import { BookPagesLetsMakeMetroService } from './book-pages-lets-make-metro.service';
import { getKidstitle } from './segments';

type TBookLetsMakeMetroCreateData = Pick<
  IBookTMLetsMakeMetroData,
  | 'heroName'
  | 'kunChan'
  | 'gender'
  | 'skinColor'
  | 'hairColor'
  | 'hairStyle'
  | 'glasses'
>;

const log = new Logger('BookLetsMakeMetroService');

@Injectable({
  providedIn: 'root',
})
export class BookLetsMakeMetroService extends BookService<
  IBookTMLetsMakeMetroData,
  TBookLetsMakeMetroCreateData
> {
  constructor(
    protected override _firebaseService: FirebaseService,
    protected override _bookPagesService: BookPagesLetsMakeMetroService,
    private _loaderService: LoaderService,
  ) {
    super(_firebaseService, _bookPagesService);
  }

  /** a method to create initial book data */
  // eslint-disable-next-line class-methods-use-this
  protected override _createBookData<
    O extends Partial<IBookTMLetsMakeMetroData>,
  >(
    /** data to create the book from */
    createBookData: TBookLetsMakeMetroCreateData,
    /** book data to owerride the defaults */
    overrideBookData: O,
  ) {
    const { kunChan } = createBookData;
    const kidstitle = getKidstitle(kunChan);
    const newBook: Omit<IBookTMLetsMakeMetroData, TCreateUserBookOmitKeys> = {
      ...createBookData,
      type: 'book',
      steps: {
        [INPUT_VALUE_STEP_CHARACTER]: {
          stepType: INPUT_VALUE_STEP_CHARACTER,
          isDone: true,
        },
        [INPUT_VALUE_STEP_STORY]: {
          stepType: INPUT_VALUE_STEP_STORY,
          isDone: false,
        },
        [INPUT_VALUE_STEP_PHOTO]: {
          stepType: INPUT_VALUE_STEP_PHOTO,
          isDone: false,
        },
        [INPUT_VALUE_STEP_MESSAGE]: {
          stepType: INPUT_VALUE_STEP_MESSAGE,
          isDone: false,
        },
      },
      // uid: undefined,
      // bookId: undefined,
      // fileUploadStatus: 0,
      multiFiles: {
        [PHOTO_TOKYO_METRO_LETS_MAKE_METRO]: {
          fileUploadStatus: 0,
        },
      },
      // createTimeStamp: _serverTimestamp,
      alias: LetsMakeMetro.alias,
      cover: LetsMakeMetro.option,
      storyLine: 'train_creation',
      messageText:
        `ゆめへの道は、ひとつじゃない。\n` +
        `まっすぐじゃなくてもいい。\n` +
        `見つからなければ、\n` +
        `早川さんみたいに土の中から進めばいい。\n` +
        `まよっても　だいじょうぶ。\n` +
        `${createBookData.heroName}${kidstitle}は、一人じゃないからね。\n`,
    };

    return { ...newBook, ...overrideBookData };
  }

  override async loadBook(
    bookId: string,
    onBookDataLoad:
      | ((bookData: IBookTMLetsMakeMetroData) => Promise<void>)
      | ((bookData: IBookTMLetsMakeMetroData) => void),
    mode: TBookMode = ROUTE_PATH_QUERY_PARAM_MODE_USER,
  ) {
    const bookData = await super.loadBook(bookId, onBookDataLoad, mode);

    this._bookPagesService.newBookInit(bookData);

    const multiFilesEntries = Object.entries(bookData.multiFiles);
    this._loaderService.pushProgressMax(multiFilesEntries.length * 2);

    await Promise.all(
      multiFilesEntries.map(async ([photoName, data]) => {
        if (data.fileUploadStatus === 10) {
          log.info('loading', photoName);
          await this._multiFileLoad(photoName);
          log.info('loaded', photoName);
        }

        this._loaderService.pushProgressValue(2);
      }),
    );

    this.textChangeSubscribe.next();
    this.messageTextChangeSubscribe.next();

    return bookData;
  }
}
